import { Category, Stat } from '../tags/Tags';

/**
 * One section of the card report: its heading, an optional line of guidance,
 * and its content. Pass `headingRef` when something needs to send the reader
 * here — focusing the heading moves the caret and scrolls it into view.
 */
export function Section({ title, hint, headingRef, children }) {
    return (
        <section className="cardInfoSection">
            <h3 className="cardInfoSectionTitle" ref={headingRef} tabIndex={headingRef ? -1 : undefined}>
                {title}
            </h3>
            {hint && <p className="cardInfoSectionHint">{hint}</p>}
            {children}
        </section>
    );
}

/**
 * React keys for a list of `{ key?, label, value? }` items: the item's own
 * `key` when it has one, else its label and value. Repeats (two "Flag" tags
 * with the same value) get an occurrence suffix so every key stays unique.
 */
function listKeys(items) {
    const seen = new Map();
    return items.map((item) => {
        const base = item.key ?? `${item.label}-${item.value ?? ''}`;
        const count = seen.get(base) || 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base}-${count}`;
    });
}

/** Label/value stats, e.g. "Affects: you". Renders nothing for an empty list. */
export function TagList({ tags }) {
    if (tags.length === 0) return null;
    const keys = listKeys(tags);
    return (
        <ul className="cardInfoTags">
            {tags.map((tag, idx) => (
                <li key={keys[idx]}>
                    <Stat label={tag.label} value={tag.value} />
                </li>
            ))}
        </ul>
    );
}

/** Category names (keywords, roles) with an optional caption. Renders nothing for an empty list. */
export function ChipList({ label, items }) {
    if (items.length === 0) return null;
    const keys = listKeys(items);
    return (
        <>
            {label && <p className="cardInfoChipLabel">{label}</p>}
            <ul className="cardInfoChips">
                {items.map((item, idx) => (
                    <li key={keys[idx]}>
                        <Category label={item.label} />
                    </li>
                ))}
            </ul>
        </>
    );
}
